import React from "react";
import { CoachRecording } from "../../types/coach-recording.entity";
import { UpdateCoachApplicationAction } from "../../types";
import { showToast } from "src/store/toast.store";
import { useUpdateCoachApplicationStatusMutation } from "../../coach-recordings.service";
import { useAppDispatch } from "src/store";
// @ts-ignore - sweetalert2 has no types in current setup
import Swal from 'sweetalert2';

type ActionButtonsProps = {
  record: CoachRecording;
};

export const ActionButtons: React.FC<ActionButtonsProps> = ({ record }) => {
  const dispatch = useAppDispatch();
  const [updateStatus, { isLoading }] = useUpdateCoachApplicationStatusMutation();

  const handleUpdate = async (action: UpdateCoachApplicationAction) => {
    let actionText = '';
    let actionTextPast = '';
    let confirmText = '';
    let confirmButtonColor = '';

    // Set text and colors based on action
    switch (action) {
      case 'SET_TO_UNDER_REVIEW':
        actionText = 'start review';
        actionTextPast = 'review started';
        confirmText = 'Are you sure you want to start reviewing this coach application?';
        confirmButtonColor = '#3b82f6';
        break;
      case 'APPROVE':
        actionText = 'approve';
        actionTextPast = 'approved';
        confirmText = 'Are you sure you want to approve this coach application?';
        confirmButtonColor = '#10b981';
        break;
      case 'REJECT':
        actionText = 'reject';
        actionTextPast = 'rejected';
        confirmText = 'Are you sure you want to reject this coach application?';
        confirmButtonColor = '#ef4444';
        break;
    }

    // Show confirmation dialog
    const result = await Swal.fire({
      title: `Confirm ${actionText}`,
      text: confirmText,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: confirmButtonColor,
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${actionText}`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        // Show loading state
        Swal.fire({
          title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)}ing...`,
          text: `Please wait while we ${actionText} the coach application...`,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await updateStatus({ id: record.id, action }).unwrap();
        
        // Close the loading state first
        Swal.close();
        
        // Show success message only for approve/reject actions, not for start review
        if (action !== 'SET_TO_UNDER_REVIEW') {
          Swal.fire({
            title: 'Success! 🎉',
            text: `Coach application ${actionTextPast} successfully`,
            icon: 'success',
            confirmButtonColor: '#3b82f6'
          });
        }
        
        // Also show toast notification
        dispatch(
          showToast({
            type: "success",
            message: `Coach ${actionTextPast} successfully`,
          })
        );
        

      } catch (error) {
        // Show error message
        Swal.fire({
          title: 'Error! ❌',
          text: `Failed to ${actionText} coach application. Please try again.`,
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
        
        // Also show toast notification
        dispatch(
          showToast({
            type: "error",
            message: "Failed to update status",
          })
        );
      }
    }
  };

  if (record.manualReviewStatus === "PENDING") {
    return (
      <div className="text-center mt-6">
        <button
          onClick={() => handleUpdate("SET_TO_UNDER_REVIEW")}
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
        >
          Start Review
        </button>
      </div>
    );
  }

  if (record.manualReviewStatus === "UNDER_REVIEW") {
    return (
      <div className="flex justify-center gap-4 mt-6">
        <button
          onClick={() => handleUpdate("APPROVE")}
          className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
        >
          ✅ Approve Coach
        </button>
        <button
          onClick={() => handleUpdate("REJECT")}
          className="bg-red-500 hover:bg-red-600 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
        >
          ❌ Reject Coach
        </button>
      </div>
    );
  }

  return null;
};
